// Fett/Kursiv/Unterstrichen, Ueberschrift, Link — dieselbe Werkzeugzeile im
// Compose-Fenster wie im Vorlagen-Editor (`EmailTemplateEditor`). Ein Bauteil, damit
// beide nicht getrennt auseinanderdriften.
//
// `fieldRef` zeigt auf das contenteditable, das formatiert wird; `onChange` bekommt
// danach dessen `innerHTML`. Bild, Anhang und Senden bleiben beim Aufrufer — die sind
// nur im Compose-Fenster sinnvoll.
import React, { useCallback, useEffect, useState } from "react";
import { Button, Dropdown, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { plainToHtml, withScheme } from "./emailHtml";

// Absatzformate der Werkzeugzeile. EINE Liste, weil derselbe Eintrag auch die
// Aufschrift des Knopfes liefert — sonst driften Menue und Anzeige auseinander.
const BLOCKS = [
  ["h1", "Heading 1"],
  ["h2", "Heading 2"],
  ["h3", "Heading 3"],
  ["p", "Normal text"],
];

// Einzug ueber den Browser: in einer Liste wird der Punkt eine Ebene tiefer
// verschachtelt, sonst kommt ein `blockquote` mit `margin-left` drumherum. Der Abstand
// MUSS inline stehen — Bootstraps `blockquote { margin: 0 0 1rem }` nimmt einem
// nackten blockquote den Einzug, und in der Mail zaehlt ohnehin nur Inline-CSS.
// `styleWithCSS` gilt fuers ganze Dokument, deshalb nur fuer diesen einen Befehl an.
export const execEditorCommand = (command, value) => {
  if (command !== "indent" && command !== "outdent") {
    document.execCommand(command, false, value);
    return;
  }
  document.execCommand("styleWithCSS", false, true);
  document.execCommand(command);
  document.execCommand("styleWithCSS", false, false);
};

// Liegt der Cursor in einem Element, auf das `test` zutrifft?
const caretInside = (field, test) => {
  let node = window.getSelection()?.anchorNode;
  while (node && node !== field) {
    if (node.nodeType === 1 && test(node)) return true;
    node = node.parentNode;
  }
  return false;
};

const isIndentedBlock = (node) =>
  node.nodeName === "LI" ||
  node.nodeName === "BLOCKQUOTE" ||
  parseFloat(node.style.marginLeft) > 0;

/**
 * Tab rueckt ein, Shift+Tab aus — fuer `onKeyDown` eines contenteditable.
 *
 * In einem LEEREN Feld bleibt Tab beim Browser und fuehrt zum naechsten Feld — wer ein
 * Formular per Tastatur ausfuellt, landet dort und will weiter. Shift+Tab ohne Einzug
 * fuehrt ebenso zurueck aus dem Feld heraus.
 * Ein schon behandeltes Tab (Auswahlliste offen) wird nicht angefasst — deshalb NACH
 * dem eigenen Handler aufrufen.
 */
export const indentOnTab = (event, onChange) => {
  if (event.key !== "Tab" || event.defaultPrevented) return;
  if (event.ctrlKey || event.altKey || event.metaKey) return;
  const field = event.currentTarget;
  const allowed = event.shiftKey
    ? caretInside(field, isIndentedBlock)
    : field.textContent.trim() !== "";
  if (!allowed) return;
  event.preventDefault();
  execEditorCommand(event.shiftKey ? "outdent" : "indent");
  onChange(field.innerHTML);
};

const EmailFormatToolbar = ({ fieldRef, onChange }) => {
  const { t } = useTranslation(["components", "common"]);

  // Was gerade an der Cursorstelle gilt — damit Fett/Kursiv/Unterstrichen gedrueckt
  // aussehen und der Formatknopf das aktuelle Format anzeigt.
  // ponytail: `queryCommand*` statt eigener DOM-Analyse. Ceiling: veraltete API, aber
  // dieselbe, mit der auch geschrieben wird — faellt sie, faellt der Editor sowieso.
  const [format, setFormat] = useState({ block: "p" });
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  // Der `a` unter dem Cursor, falls einer da ist — dann bearbeitet der Knopf ihn statt
  // einen neuen anzulegen. Ein DOM-Knoten im Zustand ist hier richtig: das
  // contenteditable-DOM gehoert nicht React, es bleibt ueber ein Rendern hinweg stehen.
  const [editingLink, setEditingLink] = useState(null);
  // Der Cursor MUSS gemerkt werden: sobald in ein Eingabefeld getippt wird, ist die
  // Auswahl im Text weg — ohne die gespeicherte Stelle landete der Link am Ende.
  const savedRange = React.useRef(null);

  const readFormat = useCallback(() => {
    const field = fieldRef.current;
    const selection = window.getSelection();
    if (!field || !selection?.anchorNode || !field.contains(selection.anchorNode)) return;
    const block = String(document.queryCommandValue("formatBlock") || "").toLowerCase();
    setFormat({
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      block: BLOCKS.some(([tag]) => tag === block) ? block : "p",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldRef]);

  // Am Dokument, nicht am Feld: die Auswahl aendert sich auch per Tastatur und ueber
  // Klicks, die kein Feld-Ereignis ausloesen.
  useEffect(() => {
    document.addEventListener("selectionchange", readFormat);
    return () => document.removeEventListener("selectionchange", readFormat);
  }, [readFormat]);

  // Alles, was den Text veraendert, laeuft ueber diese eine Stelle: execCommand
  // braucht eine Auswahl IM Feld. Steht der Cursor woanders (Klick auf einen Knopf
  // hat den Fokus geholt), wird ans Ende gesetzt — der harmlose Fall.
  const exec = (command, value) => {
    const field = fieldRef.current;
    if (!field) return;
    const selection = window.getSelection();
    if (!selection?.anchorNode || !field.contains(selection.anchorNode)) {
      field.focus();
      const range = document.createRange();
      range.selectNodeContents(field);
      range.collapse(false);
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
    execEditorCommand(command, value);
    onChange(field.innerHTML);
    readFormat();
  };

  // Steht der Cursor IN einem Link, ist der gemeint — auch ohne Markierung, denn
  // genau das ist der Fall „ich will den Link da aendern".
  const linkAtCaret = () => {
    const selection = window.getSelection();
    let node = selection?.anchorNode;
    if (!node || !fieldRef.current?.contains(node)) return null;
    while (node && node !== fieldRef.current) {
      if (node.nodeName === "A") return node;
      node = node.parentNode;
    }
    return null;
  };

  // Markierter Text wird als Linktext vorgeschlagen — das ist der haeufige Fall: Wort
  // markieren, Knopf druecken, Adresse einsetzen. Liegt der Cursor in einem
  // bestehenden Link, stehen dessen Text und Adresse schon in den Feldern.
  const openLinkForm = () => {
    const selection = window.getSelection();
    const inField =
      selection?.rangeCount && fieldRef.current?.contains(selection.anchorNode);
    const existing = linkAtCaret();
    setEditingLink(existing);
    if (existing) {
      // Der GANZE Link wird gemerkt, nicht die Cursorstelle darin: beim Einfuegen wird
      // er ersetzt und nicht ein zweiter hineingeschachtelt.
      const range = document.createRange();
      range.selectNode(existing);
      savedRange.current = range;
      setLinkText(existing.textContent || "");
      setLinkUrl(existing.getAttribute("href") || "");
      setLinkOpen(true);
      return;
    }
    savedRange.current = inField ? selection.getRangeAt(0).cloneRange() : null;
    setLinkText(inField ? String(selection).trim() : "");
    setLinkUrl("");
    setLinkOpen(true);
  };

  // Nur die Verlinkung geht weg, der Text bleibt stehen — `unlink` braucht dafuer eine
  // Auswahl, die den Link umfasst.
  const removeLink = () => {
    const anchor = editingLink;
    setLinkOpen(false);
    if (!anchor) return;
    const range = document.createRange();
    range.selectNodeContents(anchor);
    const selection = window.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    exec("unlink");
  };

  const insertLink = () => {
    const url = withScheme(linkUrl);
    if (!url) return;
    if (savedRange.current) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedRange.current);
    }
    // Adresse ins Attribut, Text als Inhalt — beides escapt: ein Anfuehrungszeichen in
    // der Adresse oder ein `<` im Text wuerde sonst das Markup aufbrechen.
    exec(
      "insertHTML",
      `<a href="${url.replace(/"/g, "&quot;")}">${plainToHtml(
        linkText.trim() || linkUrl.trim()
      )}</a>`
    );
    setLinkOpen(false);
  };

  return (
    <>
      {/* `onMouseDown` verhindert ueberall den Fokuswechsel — sonst ist die Auswahl im
          Text weg, bevor der Befehl laeuft. */}
      {[
        ["bold", "format-bold", t("Bold")],
        ["italic", "format-italic", t("Italic")],
        ["underline", "format-underline", t("Underline")],
      ].map(([command, icon, label]) => (
        <Button
          key={command}
          variant="link"
          size="sm"
          className={`text-body px-1 rounded${format[command] ? " bg-light" : ""}`}
          title={label}
          aria-label={label}
          aria-pressed={!!format[command]}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => exec(command)}
        >
          <i className={`mdi mdi-${icon}`} />
        </Button>
      ))}

      {[
        ["outdent", "format-indent-decrease", `${t("Decrease indent")} (Shift+Tab)`],
        ["indent", "format-indent-increase", `${t("Increase indent")} (Tab)`],
      ].map(([command, icon, label]) => (
        <Button
          key={command}
          variant="link"
          size="sm"
          className="text-body px-1"
          title={label}
          aria-label={label}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => exec(command)}
        >
          <i className={`mdi mdi-${icon}`} />
        </Button>
      ))}

      <Dropdown drop="up">
        <Dropdown.Toggle
          variant="link"
          size="sm"
          className="text-body px-1 text-decoration-none"
          title={t("Heading")}
          onMouseDown={(event) => event.preventDefault()}
        >
          {/* Die Aufschrift IST das aktuelle Format — ohne sie muesste man raten. */}
          {t(BLOCKS.find(([tag]) => tag === format.block)?.[1] || "Normal text")}
        </Dropdown.Toggle>
        <Dropdown.Menu>
          {BLOCKS.map(([tag, key]) => (
            <Dropdown.Item
              key={tag}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => exec("formatBlock", `<${tag}>`)}
              active={format.block === tag}
            >
              {t(key)}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      {/* Markierter Text wird als Linktext vorgeschlagen, sonst steht die Adresse
          selbst da. Knopf und Fenster liegen zusammen, damit das Fenster absolut
          UEBER dem Knopf stehen kann. `Overlay`/`Popover` von react-bootstrap ginge
          NICHT im Modal: das portalt an `document.body`, und das Modal holt den
          Fokus aus allem zurueck, was ausserhalb seines DOM liegt (`enforceFocus`) —
          in den Eingabefeldern liess sich dann nicht tippen.

          Liegt der Cursor in einem bestehenden Link, bearbeitet dasselbe Kaestchen
          ihn — mit „Link entfernen" daneben.

          ponytail: eigenes Kaestchen statt einer Popover-Bibliothek. Ceiling: es
          schliesst per Escape, erneutem Klick und nach dem Einfuegen, aber nicht
          beim Klick irgendwo daneben. */}
      <span className="position-relative d-inline-block">
        <Button
          variant="link"
          size="sm"
          className="text-body px-1"
          title={t("Insert link")}
          aria-label={t("Insert link")}
          // Fokus im Text lassen: die Auswahl wird beim Oeffnen gemerkt, und ohne das
          // waere sie schon weg, bevor sie gemerkt werden kann.
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => (linkOpen ? setLinkOpen(false) : openLinkForm())}
        >
          <i className="mdi mdi-link-variant" />
        </Button>

        {linkOpen && (
          <div
            className="position-absolute bg-white border rounded shadow p-2"
            style={{ bottom: "100%", left: 0, marginBottom: 8, width: 260, zIndex: 5 }}
            // Enter fuegt ein, Escape schliesst — in beiden Feldern gleich.
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                insertLink();
              }
              if (event.key === "Escape") {
                event.preventDefault();
                setLinkOpen(false);
              }
            }}
          >
            <Form.Control
              size="sm"
              className="mb-1"
              placeholder={t("Link text")}
              value={linkText}
              onChange={(event) => setLinkText(event.target.value)}
            />
            <Form.Control
              size="sm"
              type="url"
              autoFocus
              placeholder={t("Link address")}
              value={linkUrl}
              onChange={(event) => setLinkUrl(event.target.value)}
            />
            <div className="d-flex justify-content-end gap-2 mt-2">
              {editingLink && (
                <Button size="sm" variant="outline-danger" onClick={removeLink}>
                  {t("Remove link")}
                </Button>
              )}
              <Button size="sm" onClick={insertLink} disabled={!linkUrl.trim()}>
                {editingLink ? t("Apply") : t("Insert")}
              </Button>
            </div>
          </div>
        )}
      </span>
    </>
  );
};

export default EmailFormatToolbar;
